import React from "react";
import { motion } from "framer-motion";
import { MessageSquare, ShieldCheck } from "lucide-react";
import ServiceCard from "./ServiceCard";

const services = [
  {
    id: "whatsapp-api",
    icon: MessageSquare,
    title: "WhatsApp API Services",
    description:
      "Connect your business with customers through reliable WhatsApp APIs for transactional messages, notifications, customer communication, and automated workflows.",
    features: [
      "Automated WhatsApp messaging",
      "Transactional notifications",
      "Customer communication",
      "API-based integration",
    ],
    link: "/services/api/whatsapp-api",
  },
  {
    id: "sms-otp",
    icon: ShieldCheck,
    title: "SMS OTP Services",
    description:
      "Secure your applications with fast and reliable SMS OTP APIs for login verification, registration, mobile number verification, password recovery, and authentication.",
    features: [
      "OTP generation and delivery",
      "Login & registration verification",
      "Mobile number verification",
      "Password recovery",
    ],
    link: "/services/api/sms-otp",
  },
];

const ServicesGrid = () => {
  return (
    <section
      id="api-services"
      className="
        relative
        overflow-hidden
        theme-bg-primary
        py-6
        sm:py-8
        md:py-10
        lg:py-14
      "
    >
      {/* =====================================================
          BACKGROUND GLOW
      ====================================================== */}

      <div
        className="
          pointer-events-none
          absolute
          -left-32
          top-24
          h-56
          w-56
          rounded-full
          bg-orange-500/[0.05]
          blur-[110px]
          sm:h-72
          sm:w-72
          sm:blur-[130px]
        "
      />

      <div
        className="
          pointer-events-none
          absolute
          -right-32
          bottom-10
          h-56
          w-56
          rounded-full
          bg-orange-500/[0.04]
          blur-[110px]
          sm:h-72
          sm:w-72
          sm:blur-[130px]
        "
      />

      {/* =====================================================
          MAIN CONTAINER
      ====================================================== */}

      <div
        className="
          relative
          mx-auto
          w-full
          max-w-7xl
          px-4
          sm:px-6
          lg:px-8
        "
      >
        {/* =================================================
            SECTION HEADER
        ================================================== */}

        <motion.div
          initial={{
            opacity: 0,
            y: 20,
          }}
          whileInView={{
            opacity: 1,
            y: 0,
          }}
          transition={{
            duration: 0.6,
            ease: "easeOut",
          }}
          viewport={{
            once: true,
            amount: 0.2,
          }}
          className="
            mx-auto
            mb-9
            max-w-5xl
            text-center
            sm:mb-10
            lg:mb-12
          "
        >
          {/* Label */}

          <span
            className="
              inline-flex
              items-center
              rounded-full
              border
              border-orange-500/20
              bg-orange-500/[0.08]
              px-3
              py-1.5
              text-[10px]
              font-semibold
              uppercase
              tracking-[2px]
              sm:px-4
              sm:py-2
              sm:text-xs
              sm:tracking-[2.5px]
            "
            style={{
              color: "var(--brand-light)",
            }}
          >
            Our API Services
          </span>

          {/* Heading */}

          <h2
            className="
              mt-4
              text-2xl
              font-bold
              leading-tight
              tracking-tight
              sm:mt-5
              sm:text-3xl
              md:text-4xl
              lg:text-5xl
            "
            style={{
              color: "var(--text-primary)",
            }}
          >
            Connect Your Business{" "}
            <span
              style={{
                color: "var(--brand-primary)",
              }}
            >
              With Reliable APIs
            </span>
          </h2>

          {/* Description */}

          <p
            className="
              mx-auto
              mt-3
              max-w-7xl
              text-xs
              leading-5
              sm:mt-4
              sm:text-sm
              sm:leading-6
              md:text-base
              md:leading-7
            "
            style={{
              color: "var(--text-secondary)",
            }}
          >
            Build connected customer experiences with communication APIs
            designed for secure authentication, messaging, notifications,
            and automated business workflows.
          </p>
        </motion.div>

        {/* =================================================
            SERVICES GRID
        ================================================== */}

        <div
          className="
            grid
            grid-cols-1
            gap-4
            sm:grid-cols-2
            sm:gap-5
            lg:gap-6
            xl:grid-cols-2
          "
        >
          {services.map((service, index) => (
            <ServiceCard
              key={service.id}
              service={service}
              index={index}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

export default ServicesGrid;